import { BookRepository } from '../repositories/bookRepository';
import { FireBook, FireInsight } from '../models/FirestoreModels';
import { API_BASE_URL } from './firebaseFunctionsAPI';
import { sqliteCacheService } from './sqliteCacheService';

/**
 * Service response type for consistent error handling
 */
export interface ServiceResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Cache configuration
 * 
 * IMPORTANT: Books and insights are cached separately for flexibility:
 * - book_v2_{id}_{lang}: Just the book metadata (for lists, search, etc.)
 * - insights_v2_{id}_{lang}: Array of insights for that book (for detail view)
 * 
 * This allows:
 * - Category screens to cache books without loading insights
 * - Book detail screens to load insights only if not cached
 * - Efficient reuse across different screens
 * 
 * Version history:
 * - v1 (book_/insights_): Stored BookDetail { book, insights } together
 * - v2 (book_v2_/insights_v2_): Stores book and insights separately
 */
const CACHE_CONFIG = {
  BOOK_PREFIX: 'book_v2_',
  INSIGHTS_PREFIX: 'insights_v2_',
  FEATURED_KEY: 'featured_books_v2_',
  TTL: 3600000, // 1 hour in milliseconds
};

/**
 * Book detail data including insights
 */
export interface BookDetail {
  book: FireBook;
  insights: FireInsight[];
}

/**
 * Service layer for book operations
 * Handles business logic, caching, and data transformation
 */
export class BookService {
  // In-memory cache for current session
  private static memoryCache = new Map<string, { data: any; timestamp: number }>();
  private static sqliteInitPromise: Promise<void> | null = null;

  private static ensureSQLiteInit(): Promise<void> {
    if (!this.sqliteInitPromise) {
      this.sqliteInitPromise = sqliteCacheService.init();
    }
    return this.sqliteInitPromise;
  }

  /**
   * Get a book with its insights
   * Uses separate caches for book and insights to avoid conflicts
   */
  static async getBookDetail(
    bookId: string, 
    languageCode: string = 'en'
  ): Promise<ServiceResponse<BookDetail>> {
    try {
      await this.ensureSQLiteInit();
      const bookCacheKey = `${CACHE_CONFIG.BOOK_PREFIX}${bookId}_${languageCode}`;
      const insightsCacheKey = `${CACHE_CONFIG.INSIGHTS_PREFIX}${bookId}_${languageCode}`;
      
      let book: FireBook | null = null;
      let insights: FireInsight[] | null = null;
      
      // Memory cache first
      const memCachedBook = this.getFromMemoryCache(bookCacheKey);
      if (memCachedBook) {
        book = memCachedBook;
      }
      const memCachedInsights = this.getFromMemoryCache(insightsCacheKey);
      if (memCachedInsights) {
        insights = memCachedInsights;
      }
      
      // SQLite cache
      if (!book) {
        const cachedBook = await sqliteCacheService.getBook(bookId, languageCode);
        if (cachedBook) {
          book = cachedBook;
          this.setMemoryCache(bookCacheKey, cachedBook);
        }
      }
      if (!insights) {
        const cachedInsights = await sqliteCacheService.getInsights(bookId, languageCode);
        if (cachedInsights) {
          insights = cachedInsights;
          this.setMemoryCache(insightsCacheKey, cachedInsights);
        }
      }
      
      // If both cached, return
      if (book && insights) {
        return { success: true, data: { book, insights } };
      }

      // Fetch book if missing
      if (!book) {
        book = await BookRepository.getBookById(bookId, languageCode);
        if (!book) {
          return { 
            success: false, 
            error: `Book not found: ${bookId}` 
          };
        }
        this.setMemoryCache(bookCacheKey, book);
      }

      // Fetch insights if missing
      if (!insights) {
        insights = await BookRepository.getInsightsByIds(
          book.insightIds || [], 
          languageCode
        );
        this.setMemoryCache(insightsCacheKey, insights);
      }

      // Persist to SQLite (book + insights)
      await sqliteCacheService.saveBooks([book], languageCode);
      if (insights && insights.length > 0) {
        await sqliteCacheService.saveInsights(insights, languageCode);
      }

      return { success: true, data: { book, insights: insights || [] } };
    } catch (error) {
      console.error('BookService.getBookDetail error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to fetch book details' 
      };
    }
  }

  /**
   * Get multiple books by IDs in parallel (batch operation)
   * More efficient than calling getBookDetail multiple times
   * 
   * @param onBookLoaded Optional callback to receive books as they load (for progressive UI)
   */
  static async getBooksById(
    bookIds: string[],
    languageCode: string = 'en',
    onBookLoaded?: (book: FireBook) => void
  ): Promise<ServiceResponse<FireBook[]>> {
    try {
      if (bookIds.length === 0) {
        return { success: true, data: [] };
      }

      await this.ensureSQLiteInit();
      console.log(`📦 Service batch fetching ${bookIds.length} books`);

      const cachedBooks: FireBook[] = [];
      const remainingForSqlite: string[] = [];

      // Memory cache pass
      for (const bookId of bookIds) {
        const cacheKey = `${CACHE_CONFIG.BOOK_PREFIX}${bookId}_${languageCode}`;
        const memCached = this.getFromMemoryCache(cacheKey);
        if (memCached) {
          cachedBooks.push(memCached);
          if (onBookLoaded) onBookLoaded(memCached);
        } else {
          remainingForSqlite.push(bookId);
        }
      }

      // SQLite pass
      let sqliteBooks: FireBook[] = [];
      if (remainingForSqlite.length > 0) {
        sqliteBooks = await sqliteCacheService.getBooks(remainingForSqlite, languageCode);
        sqliteBooks.forEach(book => {
          const cleanId = book.id.includes('_') ? book.id.split('_')[0] : book.id;
          const cacheKey = `${CACHE_CONFIG.BOOK_PREFIX}${cleanId}_${languageCode}`;
          this.setMemoryCache(cacheKey, book);
          if (onBookLoaded) onBookLoaded(book);
        });
      }

      const loadedIds = new Set([...cachedBooks, ...sqliteBooks].map(b => b.id.includes('_') ? b.id.split('_')[0] : b.id));
      const missingIds = bookIds.filter(id => !loadedIds.has(id));

      // Firestore pass
      let fetchedBooks: FireBook[] = [];
      if (missingIds.length > 0) {
        fetchedBooks = await BookRepository.getBooksById(missingIds, languageCode);
        // Cache to SQLite + memory
        await sqliteCacheService.saveBooks(fetchedBooks, languageCode);
        fetchedBooks.forEach(book => {
          const cleanId = book.id.includes('_') ? book.id.split('_')[0] : book.id;
          const cacheKey = `${CACHE_CONFIG.BOOK_PREFIX}${cleanId}_${languageCode}`;
          this.setMemoryCache(cacheKey, book);
          if (onBookLoaded) onBookLoaded(book);
        });
      }

      const allBooks = [...cachedBooks, ...sqliteBooks, ...fetchedBooks];
      console.log(`✅ Batch complete: ${allBooks.length} books total (${cachedBooks.length + sqliteBooks.length} from cache/sqlite)`);

      return { success: true, data: allBooks };
    } catch (error) {
      console.error('BookService.getBooksById error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to batch fetch books'
      };
    }
  }

  /**
   * Get personalized book recommendations for a user
   * TODO: Implement when user profile AI recommendations are available
   */
  static async getPersonalizedRecommendations(
    userId: string,
    languageCode: string = 'en',
    limit: number = 10
  ): Promise<ServiceResponse<FireBook[]>> {
    try {
      // TODO: Fetch from user profile's AI recommendations
      // For now, fallback to featured books
      console.log('Personalized recommendations not yet implemented, using featured books');
      return this.getFeaturedBooks(languageCode, limit);
    } catch (error) {
      console.error('BookService.getPersonalizedRecommendations error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to fetch personalized recommendations' 
      };
    }
  }

  /**
   * Get featured books
   */
  static async getFeaturedBooks(
    languageCode: string = 'en',
    limit: number = 10
  ): Promise<ServiceResponse<FireBook[]>> {
    try {
      const cacheKey = `${CACHE_CONFIG.FEATURED_KEY}${languageCode}_${limit}`;
      
      // Check memory cache
      const memCached = this.getFromMemoryCache(cacheKey);
      if (memCached) {
        return { success: true, data: memCached };
      }

      // Fetch from repository
      const books = await BookRepository.getFeaturedBooks(languageCode, limit);

      // Cache in memory and SQLite (books only)
      this.setMemoryCache(cacheKey, books);
      await this.ensureSQLiteInit();
      await sqliteCacheService.saveBooks(books, languageCode);

      return { success: true, data: books };
    } catch (error) {
      console.error('BookService.getFeaturedBooks error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to fetch featured books' 
      };
    }
  }

  /**
   * Get popular books (by users practicing)
   */
  static async getPopularBooks(
    languageCode: string = 'en',
    limit: number = 10
  ): Promise<ServiceResponse<FireBook[]>> {
    try {
      const cacheKey = `popular_books_${languageCode}_${limit}`;
      
      // Check memory cache
      const memCached = this.getFromMemoryCache(cacheKey);
      if (memCached) {
        return { success: true, data: memCached };
      }

      // Fetch from repository
      const books = await BookRepository.getPopularBooks(languageCode, limit);

      // Cache in memory and SQLite
      this.setMemoryCache(cacheKey, books);
      await this.ensureSQLiteInit();
      await sqliteCacheService.saveBooks(books, languageCode);

      return { success: true, data: books };
    } catch (error) {
      console.error('BookService.getPopularBooks error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to fetch popular books' 
      };
    }
  }


  /**
   * Get all books for a language (no limit)
   */
  static async getAllBooks(
    languageCode: string = 'en'
  ): Promise<ServiceResponse<FireBook[]>> {
    try {
      const cacheKey = `all_books_${languageCode}`;
      
      // Check memory cache
      const memCached = this.getFromMemoryCache(cacheKey);
      if (memCached) {
        return { success: true, data: memCached };
      }

      // Fetch from repository
      const books = await BookRepository.getAllBooks(languageCode);

      // Cache for 30 minutes (books list doesn't change often)
      this.setMemoryCache(cacheKey, books, 1800000); // 30 minutes

      return { success: true, data: books };
    } catch (error) {
      console.error('BookService.getAllBooks error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to fetch all books' 
      };
    }
  }

  /**
   * Unified vector search for books and/or insights
   * Uses backend vectorSearch function with semantic search
   * 
   * Returns placeholder results immediately with minimal data from vector search,
   * then hydrates with full Firestore data in parallel for optimal performance
   */
  static async vectorSearch(
    query: string,
    types: ('book' | 'insight')[] = ['book', 'insight'],
    languageCode: string = 'en',
    topK: number = 10,
    minPerType?: number,
    options?: {
      audiences?: string[];
      audienceFilterMode?: 'strict' | 'off';
    }
  ): Promise<ServiceResponse<{ books: FireBook[]; insights: FireInsight[] }>> {
    try {
      if (!query || query.trim().length < 2) {
        return { success: true, data: { books: [], insights: [] } };
      }

      const url = `${API_BASE_URL}/vectorSearch`;
      
      // Use provided minPerType or default to 5 when searching multiple types
      const effectiveMinPerType = minPerType !== undefined ? minPerType : (types.length > 1 ? 5 : undefined);
      
      const body: any = { query, types, languageCode, topK };
      if (effectiveMinPerType !== undefined) {
        body.minPerType = effectiveMinPerType;
      }
      if (options && 'audiences' in options) {
        body.audiences = options.audiences ?? [];
      }
      if (options?.audienceFilterMode) {
        body.audienceFilterMode = options.audienceFilterMode;
      }
      
      console.log('🔍 Vector search:', body);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const json = await response.json();
      
      if (!json.success) {
        throw new Error(json.error || 'Vector search failed');
      }

      // Extract results
      const bookResults = json.data?.books || [];
      const insightResults = json.data?.insights || [];

      console.log(`📦 Vector search returned ${bookResults.length} book results`);

      // Fetch full book data from Firestore
      // OPTIMIZATION: Fetch all books in parallel using batch method
      // This is much faster than sequential fetches
      let books: FireBook[] = [];
      if (types.includes('book') && bookResults.length > 0) {
        const bookIds = bookResults.map((item: any) => String(item.id));
        const booksResponse = await this.getBooksById(bookIds, languageCode);
        if (booksResponse.success && booksResponse.data) {
          books = booksResponse.data;
          console.log(`✅ Fetched ${books.length}/${bookIds.length} books with full data`);
        } else {
          console.warn('⚠️ Failed to fetch books');
        }
      }

      // Insights from vector search (minimal data)
      const insights = insightResults.map((item: any) => ({
        id: String(item.id),
        bookId: item.bookId ? String(item.bookId) : undefined,
        title: item.title || `Insight ${item.id}`,
      }));

      console.log(`✅ Vector search complete: ${books.length} books, ${insights.length} insights`);

      return { 
        success: true, 
        data: { books, insights } 
      };
    } catch (error) {
      console.error('BookService.vectorSearch error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Vector search failed' 
      };
    }
  }



  /**
   * Get personalized books for Explore page
   * Filters by user's preferred categories and excludes specified books
   */
  static async getPersonalizedExploreBooks(
    userCategories: string[],
    excludeBookIds: string[],
    languageCode: string = 'en',
    limit: number = 8
  ): Promise<ServiceResponse<FireBook[]>> {
    try {
      console.log('📚 Getting personalized explore books:', {
        userCategories,
        excludeCount: excludeBookIds.length,
        limit
      });

      // Get all books for the language
      const allBooksResult = await this.getAllBooks(languageCode);
      if (!allBooksResult.success || !allBooksResult.data) {
        return { success: false, error: 'Failed to fetch books' };
      }

      const allBooks = allBooksResult.data;
      
      // Filter and score books
      const scoredBooks = allBooks
        .filter(book => !excludeBookIds.includes(book.id)) // Exclude homepage books
        .map(book => {
          const bookCategories = book.categories ? Object.keys(book.categories) : [];
          
          // Calculate category match score (case-insensitive, normalized)
          const matchCount = bookCategories.filter(bookCat => {
            const normalizedBookCat = bookCat.toLowerCase().replace(/[\s_-]/g, '');
            return userCategories.some(userCat => {
              const normalizedUserCat = userCat.toLowerCase().replace(/[\s_-]/g, '');
              return normalizedBookCat === normalizedUserCat;
            });
          }).length;
          
          // Scoring factors:
          // - Category matches (weight: 10 points each)
          // - Popularity (weight: up to 5 points based on usersPractising)
          // - Has insights (weight: 2 points)
          const categoryScore = matchCount * 10;
          const popularityScore = Math.min(5, (book.statistics?.usersPractising || 0) / 100);
          const insightScore = (book.insightIds?.length || 0) > 0 ? 2 : 0;
          
          const totalScore = categoryScore + popularityScore + insightScore;
          
          return {
            book,
            score: totalScore,
            matchCount,
            bookCategories,
          };
        })
        .filter(item => item.matchCount > 0) // Only books matching at least one category
        .sort((a, b) => {
          // Primary sort: score (descending)
          if (b.score !== a.score) return b.score - a.score;
          // Secondary sort: number of insights (descending)
          return (b.book.insightIds?.length || 0) - (a.book.insightIds?.length || 0);
        })
        .slice(0, limit);

      let books = scoredBooks.map(item => item.book);
      
      console.log('✅ Found', books.length, 'personalized books');
      if (books.length === 0 && userCategories.length > 0) {
        console.warn('⚠️ No books matched user categories:', userCategories);
        console.warn('⚠️ Sample book categories:', allBooks.slice(0, 3).map(b => ({
          title: b.title,
          categories: b.categories ? Object.keys(b.categories) : []
        })));
      }

      // Fallback strategy to avoid empty "For You" section for new users
      if (books.length === 0) {
        const DEFAULT_FALLBACK_CATEGORIES = ['psychology', 'business', 'leadership', 'productivity'];

        // Try again using default popular categories if user-provided interests produced no matches
        const normalized = (s: string) => s.toLowerCase().replace(/[\s_-]/g, '');
        const normalizedUser = new Set(userCategories.map(normalized));
        const shouldTryDefault = DEFAULT_FALLBACK_CATEGORIES.some(cat => !normalizedUser.has(normalized(cat)));

        if (shouldTryDefault) {
          const fallbackScored = allBooks
            .filter(book => !excludeBookIds.includes(book.id))
            .map(book => {
              const bookCategories = book.categories ? Object.keys(book.categories) : [];
              const matchCount = bookCategories.filter(bookCat => {
                return DEFAULT_FALLBACK_CATEGORIES.some(userCat => normalized(bookCat) === normalized(userCat));
              }).length;
              const categoryScore = matchCount * 10;
              const popularityScore = Math.min(5, (book.statistics?.usersPractising || 0) / 100);
              const insightScore = (book.insightIds?.length || 0) > 0 ? 2 : 0;
              const totalScore = categoryScore + popularityScore + insightScore;
              return { book, score: totalScore, matchCount };
            })
            .filter(item => item.matchCount > 0)
            .sort((a, b) => {
              if (b.score !== a.score) return b.score - a.score;
              return (b.book.insightIds?.length || 0) - (a.book.insightIds?.length || 0);
            })
            .slice(0, limit);

          books = fallbackScored.map(item => item.book);
          if (books.length > 0) {
            console.log('🔁 Fallback to default categories produced', books.length, 'books');
          }
        }

        // Final fallback: popular books excluding homepage and already shown
        if (books.length === 0) {
          const popularResult = await this.getPopularBooks(languageCode, limit * 2);
          if (popularResult.success && popularResult.data) {
            books = popularResult.data
              .filter(book => !excludeBookIds.includes(book.id))
              .slice(0, limit);
            console.log('✨ Using popular books as fallback for personalized section:', books.length);
          }
        }
      }

      return { success: true, data: books };
    } catch (error) {
      console.error('BookService.getPersonalizedExploreBooks error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to get personalized books' 
      };
    }
  }

  /**
   * Get new/trending books excluding specified IDs
   * Prioritizes recently added or increasingly popular books
   */
  static async getNewBooks(
    excludeBookIds: string[],
    languageCode: string = 'en',
    limit: number = 8
  ): Promise<ServiceResponse<FireBook[]>> {
    try {
      console.log('🆕 Getting new books, excluding', excludeBookIds.length, 'books');

      // Get popular books (already sorted by usersPractising)
      const popularResult = await this.getPopularBooks(languageCode, limit * 2);
      if (!popularResult.success || !popularResult.data) {
        return { success: false, error: 'Failed to fetch popular books' };
      }

      // Filter out excluded books
      const books = popularResult.data
        .filter(book => !excludeBookIds.includes(book.id))
        .slice(0, limit);

      console.log('✅ Found', books.length, 'new books');
      
      return { success: true, data: books };
    } catch (error) {
      console.error('BookService.getNewBooks error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to get new books' 
      };
    }
  }

  /**
   * Clear all book caches
   */
  static async clearCache(): Promise<void> {
    try {
      // Clear memory cache
      this.memoryCache.clear();

      // Clear SQLite cache
      await this.ensureSQLiteInit();
      await sqliteCacheService.clearAll();
    } catch (error) {
      console.error('Error clearing book cache:', error);
    }
  }

  /**
   * Invalidate cache for a specific book (both book and insights)
   */
  static async invalidateBookCache(bookId: string, languageCode: string = 'en'): Promise<void> {
    const bookCacheKey = `${CACHE_CONFIG.BOOK_PREFIX}${bookId}_${languageCode}`;
    const insightsCacheKey = `${CACHE_CONFIG.INSIGHTS_PREFIX}${bookId}_${languageCode}`;
    
    // Clear from memory cache
    this.memoryCache.delete(bookCacheKey);
    this.memoryCache.delete(insightsCacheKey);

    // Clear from SQLite
    await this.ensureSQLiteInit();
    await sqliteCacheService.deleteBook(bookId, languageCode);
    await sqliteCacheService.deleteInsightsForBook(bookId, languageCode);
  }

  // Cache utilities
  private static getFromMemoryCache(key: string): any | null {
    const cached = this.memoryCache.get(key);
    if (!cached) return null;

    const age = Date.now() - cached.timestamp;
    if (age > CACHE_CONFIG.TTL) {
      this.memoryCache.delete(key);
      return null;
    }

    return cached.data;
  }

  private static setMemoryCache(key: string, data: any, ttl?: number): void {
    this.memoryCache.set(key, {
      data,
      timestamp: Date.now(),
    });

    // Optional: Limit memory cache size
    if (this.memoryCache.size > 100) {
      const firstKey = this.memoryCache.keys().next().value;
      if (firstKey) {
        this.memoryCache.delete(firstKey);
      }
    }
  }
} 